import { useRef, useState } from 'react'
import { Fade } from 'utoniq-ui'

import { Maybe, PostDetailQuery, User } from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import {
  CommentList,
  CommentListProps,
  PostDetailBox,
  PostDetailBoxSkeleton,
  ReplyForm,
  ReplyFormRef,
  ReplyFormValues,
} from '@/components/organisms'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter } from '@/core'
import { Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loadingDeletePost?: boolean
  artistId: string
  loading: boolean
  hasNextPage: boolean
  post: Maybe<PostDetailQuery['post']>
  user: Maybe<
    Pick<User, 'id' | 'displayName' | 'formattedAccountId' | 'avatar'>
  >
  /**
   * @description
   * コメント機能を無効化しているか否か
   * 無効化している場合はコメント関連機能を非表示にする
   */
  disableReplies?: boolean
}

export type Action = {
  onLike: (isLike: boolean, likeCount: number) => Promise<void>
  onReply: (values: ReplyFormValues) => Promise<void>
  onFetchMore: () => Promise<void>
  onDeleteReply: CommentListProps['onDeleteComment']
  onReportUser: CommentListProps['onReportUser']
  onBlockUser: CommentListProps['onBlockUser']
  onDeletePost: (id: string) => Promise<void>
}

export const ConsolePostDetailTemplate: Template<State, Action> = ({
  state,
  onLike,
  onReply,
  onFetchMore,
  onDeleteReply,
  onReportUser,
  onBlockUser,
  onDeletePost,
}) => {
  const { routerNames, routerPages } = useRouter()
  const [showReply, setShowReply] = useState(true)
  const replyFormRef = useRef<ReplyFormRef>(null)

  const shouldShowReplyForm = !state.disableReplies && showReply

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsolePostDetail} />
      <ConsoleBaseLayout
        header={{
          back: true,
          defaultBackPage: routerPages.ConsolePost,
          color: 'white',
        }}
        customNavigation={
          shouldShowReplyForm ? (
            <ReplyForm
              onReply={onReply}
              userId={state.user?.id ?? ''}
              ref={replyFormRef}
            />
          ) : null
        }
      >
        {!state.loading && state?.post ? (
          <Fade>
            <PostDetailBox
              isConsole
              userId={state.user?.id ?? ''}
              post={state.post}
              loadingDeletePost={state.loadingDeletePost}
              enableEmbedHtml={true}
              onDeletePost={onDeletePost}
              onLike={() => {
                if (!state.post) return
                onLike(!!state?.post.isLike, state?.post.likeCount)
              }}
              onReply={() => setShowReply(prevState => !prevState)}
            />
          </Fade>
        ) : (
          <PostDetailBoxSkeleton />
        )}
        {!state.disableReplies && (
          <CommentList
            showReaction
            commentCount={state.post?.replyCount}
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            comments={state.post?.replies ?? null}
            user={state.user ?? null}
            onFetchMore={onFetchMore}
            onDeleteComment={onDeleteReply}
            onReportUser={onReportUser}
            onBlockUser={onBlockUser}
            hiddenNotice
            onClickReply={() => {}}
            onReplyToReply={() => {}}
          />
        )}
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}
